import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Rocket, LogOut, Search, ChevronLeft, ChevronRight, LayoutGrid,
  Clock, CheckCircle2, DollarSign, Loader2, Inbox, ArrowUpRight, Bell,
} from "lucide-react";
import { toast } from "sonner";
import { adminCheck, adminStats, adminListProjects, adminLogout, adminNotifications, adminNotificationsRead } from "../lib/api";
import { PREVIEW_STATUS_META, statusLabel } from "../data/previewStatus";
import { AdminNav } from "./AdminContinueBuilding";

const PAGE = 25;

const STATUS_OPTIONS = Object.keys(PREVIEW_STATUS_META);

const priorityColor = {
  low: "bg-gray-100 text-gray-600",
  normal: "bg-blue-50 text-blue-600",
  high: "bg-amber-50 text-amber-700",
  urgent: "bg-red-50 text-red-600",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(null);
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });

  const loadNotifs = useCallback(() => {
    adminNotifications().then(setNotifs).catch(() => {});
  }, []);

  useEffect(() => {
    adminCheck()
      .then((r) => {
        if (!r.admin) {
          setAuthorized(false);
          navigate("/");
          return;
        }
        setAuthorized(true);
        adminStats().then(setStats).catch(() => {});
        loadNotifs();
      })
      .catch(() => {
        setAuthorized(false);
        navigate("/");
      });
  }, [navigate, loadNotifs]);

  const markNotifsRead = async () => {
    await adminNotificationsRead().catch(() => {});
    loadNotifs();
  };

  const load = useCallback(
    async (pageNum, status, q) => {
      setLoading(true);
      try {
        const data = await adminListProjects({
          limit: PAGE,
          offset: (pageNum - 1) * PAGE,
          status: status || undefined,
          search: q || undefined,
        });
        setProjects(data.items);
        setTotal(data.total);
        setPage(pageNum);
      } catch {
        setProjects([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (authorized) load(1, statusFilter, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorized, statusFilter]);

  const handleLogout = async () => {
    await adminLogout();
    toast.success("Signed out of admin");
    navigate("/");
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE));

  if (authorized === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B0B0B]">
        <Loader2 className="animate-spin text-[#D4AF37]" size={30} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="border-b border-white/10 sticky top-0 z-20 bg-[#0B0B0B]/90 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                <Rocket className="text-[#D4AF37]" size={18} />
              </div>
              <div>
                <p className="font-display font-bold leading-tight">Command Center</p>
                <p className="text-xs text-[#71717A]">Preview Project Operations</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-white transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut size={16} /> Sign out
            </button>
          </div>
          <AdminNav active="projects" />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        {/* Summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="admin-stats">
          <StatCard icon={LayoutGrid} label="Total Projects" value={stats?.total_projects ?? "—"} />
          <StatCard icon={Clock} label="Active" value={stats?.active_projects ?? "—"} accent="text-amber-400" />
          <StatCard icon={CheckCircle2} label="Completed" value={stats?.completed ?? "—"} accent="text-emerald-400" />
          <StatCard
            icon={DollarSign}
            label="Revenue"
            value={stats ? `$${stats.revenue.toLocaleString()}` : "—"}
            accent="text-[#D4AF37]"
          />
        </div>

        {/* Notify Me / Waitlist notifications */}
        <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 mb-8" data-testid="admin-notifications">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Bell size={16} className="text-[#D4AF37]" />
              <p className="font-display font-semibold">Notify Me requests</p>
              {notifs.unread > 0 && (
                <span className="inline-flex items-center justify-center rounded-full bg-[#D4AF37] text-[#0F0F0F] text-[10px] font-bold px-2 py-0.5" data-testid="notif-unread-count">
                  {notifs.unread} new
                </span>
              )}
            </div>
            {notifs.unread > 0 && (
              <button onClick={markNotifsRead} className="text-xs text-[#A1A1AA] hover:text-white transition-colors" data-testid="notif-mark-read">
                Mark all read
              </button>
            )}
          </div>
          {notifs.items.length === 0 ? (
            <p className="text-sm text-[#71717A]">No waitlist signups yet.</p>
          ) : (
            <ul className="divide-y divide-white/5 max-h-56 overflow-y-auto" data-testid="notif-list">
              {notifs.items.slice(0, 25).map((n) => (
                <li key={n.id} className={`flex items-center justify-between gap-3 py-2.5 text-sm ${!n.read ? "text-white" : "text-[#A1A1AA]"}`}>
                  <span className="flex items-center gap-2 min-w-0">
                    {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] shrink-0" />}
                    <span className="font-medium truncate">{n.service}</span>
                  </span>
                  <span className="text-xs text-[#71717A] shrink-0">{n.email || "guest"} · {(n.created_at || "").slice(0, 10)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              load(1, statusFilter, search);
            }}
            className="flex-1 flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5"
          >
            <Search size={17} className="text-[#71717A]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by project name or order number..."
              className="w-full bg-transparent outline-none text-sm placeholder-[#71717A]"
              data-testid="admin-search-input"
            />
          </form>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm outline-none [&>option]:text-black"
            data-testid="admin-status-filter"
          >
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{statusLabel(s)}</option>
            ))}
          </select>
        </div>

        {/* Project queue */}
        <div className="bg-white/[0.03] border border-white/10 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="animate-spin text-[#D4AF37]" size={26} />
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-20" data-testid="admin-empty">
              <Inbox className="mx-auto text-[#3f3f46] mb-3" size={34} />
              <p className="font-display font-semibold">No projects found</p>
              <p className="text-sm text-[#71717A] mt-1">Projects appear here after a customer completes payment.</p>
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="admin-projects-table">
              <thead>
                <tr className="text-left text-[#71717A] border-b border-white/10">
                  <th className="font-medium px-5 py-3">Project</th>
                  <th className="font-medium px-5 py-3 hidden md:table-cell">Order</th>
                  <th className="font-medium px-5 py-3">Status</th>
                  <th className="font-medium px-5 py-3 hidden sm:table-cell">Progress</th>
                  <th className="font-medium px-5 py-3 hidden lg:table-cell">Priority</th>
                  <th className="font-medium px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {projects.map((p) => (
                  <tr
                    key={p.id}
                    onClick={() => navigate(`/admin/projects/${p.id}`)}
                    className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                    data-testid={`admin-project-row-${p.id}`}
                  >
                    <td className="px-5 py-4 font-medium">{p.project_name}</td>
                    <td className="px-5 py-4 text-[#A1A1AA] hidden md:table-cell">{p.order_number}</td>
                    <td className="px-5 py-4">
                      <span className="inline-flex rounded-full bg-white/10 px-2.5 py-0.5 text-xs">
                        {statusLabel(p.project_status)}
                      </span>
                    </td>
                    <td className="px-5 py-4 hidden sm:table-cell">
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1.5 rounded-full bg-white/10 overflow-hidden">
                          <div className="h-full bg-[#D4AF37]" style={{ width: `${p.progress_percentage || 0}%` }} />
                        </div>
                        <span className="text-xs text-[#71717A]">{p.progress_percentage || 0}%</span>
                      </div>
                    </td>
                    <td className="px-5 py-4 hidden lg:table-cell">
                      <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs capitalize ${priorityColor[p.priority] || priorityColor.normal}`}>
                        {p.priority || "normal"}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <ArrowUpRight size={16} className="inline text-[#71717A]" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {!loading && total > PAGE && (
          <div className="flex items-center justify-center gap-2 mt-8">
            <button
              disabled={page <= 1}
              onClick={() => load(page - 1, statusFilter, search)}
              className="inline-flex items-center gap-1 rounded-full border border-white/10 px-4 py-2 text-sm disabled:opacity-40 hover:bg-white/5 transition-colors"
              data-testid="admin-prev-btn"
            >
              <ChevronLeft size={16} /> Prev
            </button>
            <span className="text-sm text-[#71717A] px-2">Page {page} of {totalPages}</span>
            <button
              disabled={page >= totalPages}
              onClick={() => load(page + 1, statusFilter, search)}
              className="inline-flex items-center gap-1 rounded-full border border-white/10 px-4 py-2 text-sm disabled:opacity-40 hover:bg-white/5 transition-colors"
              data-testid="admin-next-btn"
            >
              Next <ChevronRight size={16} />
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, accent = "text-white" }) => (
  <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5">
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs text-[#71717A]">{label}</span>
      <Icon size={16} className={accent} />
    </div>
    <p className={`font-display text-2xl font-bold ${accent}`}>{value}</p>
  </div>
);
